/**
 * @name: Cat Hero Slider Controls
 * @author: Cursor (Agent)
 * @date: 2026-05-08
 * @description: Handles catHero tab card slider arrows, mobile dot pagination, and quick add.
 * @dependencies: index markup with catHero classes
 * @strict-mode: true
 */

(function () {
  var DOTS_MQ = '(max-width: 768px)';

  function throttleRaf(fn) {
    var ticking = false;
    return function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        fn();
      });
    };
  }

  function getActiveGroup(section) {
    var checked = section.querySelector('input[name="catTab"]:checked');
    var cardsRoot = section.querySelector('.catHero__cards');
    if (!checked || !cardsRoot) return null;
    if (checked.id === 'tab-tableware') return cardsRoot.querySelector('.catHero__cardsGroup--tableware');
    if (checked.id === 'tab-decor') return cardsRoot.querySelector('.catHero__cardsGroup--decor');
    if (checked.id === 'tab-gifts') return cardsRoot.querySelector('.catHero__cardsGroup--gifts');
    return null;
  }

  function getScrollStep(group) {
    var card = group.querySelector('.catCard');
    if (!card) return 0;
    return card.offsetWidth + 18;
  }

  function getActiveDotIndex(group) {
    var cards = group.querySelectorAll('.catCard');
    if (!cards.length) return 0;
    var midpoint = group.scrollLeft + group.clientWidth * 0.5;
    var best = 0;
    var bestDist = Infinity;
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var cMid = card.offsetLeft + card.offsetWidth * 0.5;
      var dist = Math.abs(midpoint - cMid);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  }

  function setActiveDot(dotsNav, index) {
    var buttons = dotsNav.querySelectorAll('.catHeroDots__dot');
    for (var i = 0; i < buttons.length; i++) {
      var isOn = i === index;
      buttons[i].classList.toggle('is-active', isOn);
      if (isOn) {
        buttons[i].setAttribute('aria-current', 'true');
      } else {
        buttons[i].removeAttribute('aria-current');
      }
    }
  }

  function buildDotsForPair(group, dotsNav) {
    var labelTemplate = dotsNav.getAttribute('data-dot-label') || 'Go to slide {n}';
    var cards = group.querySelectorAll('.catCard');
    var mq = window.matchMedia(DOTS_MQ);

    dotsNav.innerHTML = '';

    if (!mq.matches || cards.length <= 1) {
      dotsNav.classList.add('catHero__dots--empty');
      return;
    }

    dotsNav.classList.remove('catHero__dots--empty');

    for (var i = 0; i < cards.length; i++) {
      (function (idx) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'catHeroDots__dot' + (idx === 0 ? ' is-active' : '');
        btn.setAttribute('aria-label', labelTemplate.replace('{n}', String(idx + 1)));
        btn.setAttribute('aria-current', idx === 0 ? 'true' : 'false');
        btn.addEventListener('click', function () {
          var target = cards[idx];
          if (!target) return;
          group.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        });
        dotsNav.appendChild(btn);
      })(i);
    }

    var onScroll = throttleRaf(function () {
      setActiveDot(dotsNav, getActiveDotIndex(group));
    });

    group.addEventListener('scroll', onScroll, { passive: true });
    dotsNav._catHeroScrollCleanup = function () {
      group.removeEventListener('scroll', onScroll);
    };
  }

  function destroyDotsNav(dotsNav) {
    if (dotsNav._catHeroScrollCleanup) {
      dotsNav._catHeroScrollCleanup();
      dotsNav._catHeroScrollCleanup = null;
    }
    dotsNav.innerHTML = '';
    dotsNav.classList.add('catHero__dots--empty');
  }

  function refreshDots(cardsRoot) {
    var pairs = [
      {
        group: cardsRoot.querySelector('.catHero__cardsGroup--tableware'),
        dots: cardsRoot.querySelector('.catHero__dots--tableware')
      },
      {
        group: cardsRoot.querySelector('.catHero__cardsGroup--decor'),
        dots: cardsRoot.querySelector('.catHero__dots--decor')
      },
      {
        group: cardsRoot.querySelector('.catHero__cardsGroup--gifts'),
        dots: cardsRoot.querySelector('.catHero__dots--gifts')
      }
    ];

    for (var p = 0; p < pairs.length; p++) {
      var pair = pairs[p];
      if (!pair.group || !pair.dots) continue;
      destroyDotsNav(pair.dots);
      buildDotsForPair(pair.group, pair.dots);
      if (!pair.dots.classList.contains('catHero__dots--empty')) {
        setActiveDot(pair.dots, getActiveDotIndex(pair.group));
      }
    }
  }

  function initSection(section) {
    var cardsRoot = section.querySelector('.catHero__cards');
    var tabRadios = Array.from(section.querySelectorAll('input[name="catTab"]'));
    var leftBtn = cardsRoot ? cardsRoot.querySelector('.catHeroCards__arrow--left') : null;
    var rightBtn = cardsRoot ? cardsRoot.querySelector('.catHeroCards__arrow--right') : null;

    if (!cardsRoot || (!tabRadios.length && !leftBtn && !rightBtn)) return;

    function wireArrows(btn, direction) {
      if (!btn) return;
      btn.addEventListener('click', function () {
        var group = getActiveGroup(section);
        if (!group) return;
        var step = getScrollStep(group);
        if (!step) return;
        if (direction > 0) {
          if (group.scrollLeft + group.clientWidth >= group.scrollWidth - 5) {
            group.scrollLeft = 0;
          }
          group.scrollBy({ left: step, behavior: 'smooth' });
          return;
        }
        if (group.scrollLeft <= 0) {
          group.scrollLeft = group.scrollWidth;
        }
        group.scrollBy({ left: -step, behavior: 'smooth' });
      });
    }

    wireArrows(rightBtn, 1);
    wireArrows(leftBtn, -1);

    tabRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        var group = getActiveGroup(section);
        if (group) group.scrollLeft = 0;
        refreshDots(cardsRoot);
      });
    });

    refreshDots(cardsRoot);

    var onResize = throttleRaf(function () {
      refreshDots(cardsRoot);
    });

    window.addEventListener('resize', onResize);

    if (window.matchMedia(DOTS_MQ).addEventListener) {
      window.matchMedia(DOTS_MQ).addEventListener('change', onResize);
    } else if (window.matchMedia(DOTS_MQ).addListener) {
      window.matchMedia(DOTS_MQ).addListener(onResize);
    }
  }

  document.querySelectorAll('section.catHero').forEach(initSection);

  document.addEventListener(
    'click',
    async function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;

      var button = target.closest('.catCard__btn');
      if (!(button instanceof HTMLButtonElement)) return;

      event.preventDefault();

      var variantId = button.getAttribute('data-product-id');
      if (!variantId) return;
      if (button.getAttribute('aria-disabled') === 'true') return;

      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');

      var formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      try {
        var response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        var data = await response.json();
        if (!response.ok || data.status) {
          var message =
            data && (data.description || data.message)
              ? data.description || data.message
              : 'Unable to add product to cart';
          throw new Error(message);
        }

        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        if (theme && theme.settings && theme.settings.afterAddToCart === 'drawer') {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
        }
      } catch (error) {
        document.dispatchEvent(
          new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: { error: error instanceof Error ? error.message : 'Cart error' }
          })
        );
      } finally {
        button.classList.remove('is-loading');
        button.classList.add('is-success');
        button.removeAttribute('aria-disabled');
        setTimeout(function () {
          button.classList.remove('is-success');
        }, 1200);
      }
    },
    false
  );
})();
